/**
 * WhysSection — the iterative why-chain plus root-cause confirmation.
 *
 * Behavior:
 *   - Each existing why is a numbered, inline-editable row. Hover reveals an
 *     "×" to delete. PATCH /api/pcap/whys/:id and DELETE /api/pcap/whys/:id.
 *   - A single-line "Why?" input at the bottom appends a new why. POST
 *     /api/pcap/caps/:id/whys.
 *   - Once at least one why exists, a quiet "✨ Suggest the next why" button
 *     calls the AI helper. The suggestion lands as a faint italic line with
 *     "Use this" / "Try another" so the user stays in control.
 *   - Once three or more whys exist, a "Confirm a root cause" CTA reveals an
 *     inline form (text + category + optional evidence summary) and POSTs to
 *     /root-causes.
 *   - At nine or more whys, a soft warning suggests the user consider a
 *     branch. We never block.
 */

import React, { useState } from 'react';
import SectionCard from './SectionCard';
import AiButton from './AiButton';
import Pill from './Pill';

const API = process.env.REACT_APP_API_URL;

const ROOT_CAUSE_CATEGORIES = [
  { value: 'process',    label: 'Process' },
  { value: 'people',     label: 'People' },
  { value: 'policy',     label: 'Policy' },
  { value: 'technology', label: 'Technology' },
  { value: 'external',   label: 'External' },
];

function WhyRow({ why, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(why.text);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const commit = async () => {
    if (draft.trim() === why.text) {
      setEditing(false);
      return;
    }
    if (!draft.trim()) {
      setEditing(false);
      setDraft(why.text);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(why.id, draft.trim());
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await onDelete(why.id);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <li className="group flex gap-3 items-start">
      <span className="text-[12px] font-mono text-slate-400 w-5 pt-0.5">{why.sequence}.</span>
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              else if (e.key === 'Escape') { setDraft(why.text); setEditing(false); }
            }}
            disabled={busy}
            className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        ) : (
          <span
            role="button"
            tabIndex={0}
            onClick={() => setEditing(true)}
            onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
            className="text-sm text-slate-800 cursor-text rounded -mx-1 px-1 hover:bg-slate-50"
          >
            {why.text}
          </span>
        )}
        {error && <p className="text-[12px] text-red-700 mt-0.5">{error}</p>}
      </div>
      <button
        type="button"
        onClick={remove}
        disabled={busy}
        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-700 text-sm leading-none px-1 transition disabled:opacity-30"
        aria-label="Remove this why"
        title="Remove"
      >
        ×
      </button>
    </li>
  );
}

export default function WhysSection({ data, onRefresh }) {
  const cap = data.cap;
  const whys = data.whys || [];
  const rootCauses = data.root_causes || [];

  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null); // {text, rationale}

  const [showRcForm, setShowRcForm] = useState(false);
  const [rcText, setRcText] = useState('');
  const [rcCategory, setRcCategory] = useState('process');
  const [rcEvidence, setRcEvidence] = useState('');
  const [rcSaving, setRcSaving] = useState(false);
  const [rcError, setRcError] = useState(null);

  const addWhy = async (text) => {
    if (!text || !text.trim()) return;
    setAdding(true);
    setAddError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/whys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text.trim() }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not add');
      setNewText('');
      setAiSuggestion(null);
      onRefresh();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const editWhy = async (id, text) => {
    const res = await fetch(`${API}/api/pcap/whys/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.message || 'Could not save');
    onRefresh();
  };

  const deleteWhy = async (id) => {
    const res = await fetch(`${API}/api/pcap/whys/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.message || 'Could not delete');
    onRefresh();
  };

  const askAi = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/ai/next-why`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'AI helper unavailable');
      setAiSuggestion(d.suggestion || null);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const submitRootCause = async (e) => {
    e.preventDefault();
    if (!rcText.trim()) return;
    setRcSaving(true);
    setRcError(null);
    try {
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/root-causes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: rcText.trim(),
          category: rcCategory,
          evidence_summary: rcEvidence.trim() || null,
          source_why_ids: whys.map(w => w.id),
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not save root cause');
      setRcText('');
      setRcCategory('process');
      setRcEvidence('');
      setShowRcForm(false);
      onRefresh();
    } catch (err) {
      setRcError(err.message);
    } finally {
      setRcSaving(false);
    }
  };

  return (
    <SectionCard
      title="Why it happened"
      hint="Ask why until you reach a real root cause. There is no rule that you must stop at five."
    >
      {whys.length === 0 && (
        <p className="text-sm text-slate-400 italic mb-3">
          No why-chain yet. The first why is usually the easiest one to write.
        </p>
      )}

      {whys.length > 0 && (
        <ol className="space-y-2">
          {whys.map(w => (
            <WhyRow
              key={w.id}
              why={w}
              onSave={editWhy}
              onDelete={deleteWhy}
            />
          ))}
        </ol>
      )}

      {whys.length >= 9 && (
        <div className="mt-3 rounded-md bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[12px] text-amber-900">
          If you've gone past nine, the chain may be branching — consider
          confirming the root cause and starting a separate why on a different
          branch.
        </div>
      )}

      {/* AI suggestion */}
      {aiSuggestion && (
        <div className="mt-3 rounded-md bg-slate-50 ring-1 ring-slate-200 px-3 py-2">
          <p className="text-sm italic text-slate-600">{aiSuggestion.text}</p>
          {aiSuggestion.rationale && (
            <p className="text-[12px] text-slate-400 mt-1">{aiSuggestion.rationale}</p>
          )}
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => addWhy(aiSuggestion.text)}
              disabled={adding}
              className="px-2.5 py-1 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {adding ? 'Saving…' : 'Use this'}
            </button>
            <button
              type="button"
              onClick={askAi}
              disabled={aiLoading}
              className="px-2.5 py-1 rounded-md text-[12px] text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              Try another
            </button>
            <button
              type="button"
              onClick={() => setAiSuggestion(null)}
              className="px-2.5 py-1 rounded-md text-[12px] text-slate-500 hover:bg-slate-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Add a why */}
      <form
        onSubmit={(e) => { e.preventDefault(); addWhy(newText); }}
        className="mt-4 flex items-center gap-2"
      >
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          placeholder="Why?"
          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <button
          type="submit"
          disabled={adding || !newText.trim()}
          className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          {adding ? 'Saving…' : 'Save'}
        </button>
      </form>
      {addError && <p className="text-[12px] text-red-700 mt-1">{addError}</p>}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {whys.length >= 1 && (
          <AiButton label="Suggest the next why" onClick={askAi} loading={aiLoading} />
        )}
        {whys.length >= 3 && !showRcForm && (
          <button
            type="button"
            onClick={() => setShowRcForm(true)}
            className="px-2.5 py-1 rounded-md text-[12px] text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            Confirm a root cause
          </button>
        )}
      </div>
      {aiError && <p className="text-[12px] text-red-700 mt-1">{aiError}</p>}

      {/* Inline root-cause form */}
      {showRcForm && (
        <form onSubmit={submitRootCause} className="mt-4 rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3 space-y-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-700 mb-1">
              The root cause is…
            </label>
            <textarea
              value={rcText}
              onChange={e => setRcText(e.target.value)}
              rows={2}
              placeholder="In one or two sentences, name the underlying cause."
              className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium text-slate-700 mb-1">Category</label>
              <select
                value={rcCategory}
                onChange={e => setRcCategory(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              >
                {ROOT_CAUSE_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-700 mb-1">
                What evidence supports this? (optional)
              </label>
              <input
                value={rcEvidence}
                onChange={e => setRcEvidence(e.target.value)}
                placeholder="A short note is fine."
                className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              />
            </div>
          </div>
          {rcError && <p className="text-[12px] text-red-700">{rcError}</p>}
          <div className="flex items-center gap-2">
            <button
              type="submit"
              disabled={rcSaving || !rcText.trim()}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {rcSaving ? 'Saving…' : 'Save root cause'}
            </button>
            <button
              type="button"
              onClick={() => { setShowRcForm(false); setRcError(null); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Confirmed root causes */}
      {rootCauses.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <p className="text-[12px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
            Root cause{rootCauses.length > 1 ? 's' : ''}
          </p>
          <ul className="space-y-2">
            {rootCauses.map(rc => (
              <li key={rc.id} className="text-sm text-slate-800">
                <div className="flex items-start gap-2">
                  <span className="font-medium flex-1">{rc.text}</span>
                  <Pill tone="bg-slate-100 text-slate-700 ring-slate-200">{rc.category}</Pill>
                </div>
                {rc.evidence_summary && (
                  <p className="text-[12px] text-slate-500 mt-0.5">
                    Evidence: {rc.evidence_summary}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}
